import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, CircularProgress, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaCheck,
  FaCommentAlt,
  FaEnvelope,
  FaFlag,
  FaLock,
  FaPlus,
  FaStickyNote,
  FaTag,
  FaUndoAlt,
  FaUserEdit,
  FaUserPlus,
} from "react-icons/fa";
import { toDate } from "helpers/dateHelper";
import { queryClient } from "queryClient";
import eventsService from "services/admin/eventsService";
import { useGetTicketEvents } from "hooks/useGetTicketEvents";
import { Event } from "interfaces/entities/event.model";

interface ActivityTimelineProps {
  ticketID: number | string;
}

type DotColor = "neutral" | "info" | "success" | "warn" | "danger" | "accent";

interface RenderedEvent {
  icon: JSX.Element;
  dotColor: DotColor;
  title: string;
  body: JSX.Element | null;
}

const ChevronChip = ({
  from,
  to,
  accentTo,
}: {
  from?: React.ReactNode;
  to?: React.ReactNode;
  accentTo?: boolean;
}) => (
  <div className="tv2-tl-diff">
    <span className="tv2-tl-diff-from">{from ?? "—"}</span>
    <FaArrowRight size={9} style={{ color: "var(--tv2-text-4)" }} />
    <span className={`tv2-tl-diff-to${accentTo ? " accent" : ""}`}>{to ?? "—"}</span>
  </div>
);

const buildRendered = (event: Event, t: (key: string, opts?: any) => string): RenderedEvent => {
  const payload = event.payload || {};

  switch (event.eventType) {
    case "TICKET_CREATED":
      return {
        icon: <FaPlus size={10} />,
        dotColor: "accent",
        title: "Обращение создано",
        body: payload.subject ? <div className="tv2-tl-text">{payload.subject}</div> : null,
      };

    case "TICKET_STATUS_CHANGED":
      return {
        icon: <FaCheck size={10} />,
        dotColor: "info",
        title: "Статус изменён",
        body: (
          <ChevronChip
            from={
              payload.from
                ? t(`ticket.status.${payload.from}`, { defaultValue: payload.from })
                : null
            }
            to={t(`ticket.status.${payload.to}`, { defaultValue: payload.to })}
            accentTo
          />
        ),
      };

    case "TICKET_PRIORITY_CHANGED":
      return {
        icon: <FaFlag size={10} />,
        dotColor: "warn",
        title: "Приоритет изменён",
        body: (
          <ChevronChip
            from={
              payload.from ? t(`priority.${payload.from}`, { defaultValue: payload.from }) : null
            }
            to={t(`priority.${payload.to}`, { defaultValue: payload.to })}
            accentTo
          />
        ),
      };

    case "TICKET_TYPE_CHANGED":
      return {
        icon: <FaTag size={10} />,
        dotColor: "neutral",
        title: "Тип обращения изменён",
        body: (
          <ChevronChip
            from={
              payload.from ? t(`ticket.type.${payload.from}`, { defaultValue: payload.from }) : null
            }
            to={t(`ticket.type.${payload.to}`, { defaultValue: payload.to })}
          />
        ),
      };

    case "TICKET_CATEGORY_CHANGED":
      return {
        icon: <FaTag size={10} />,
        dotColor: "neutral",
        title: "Тематика изменена",
        body: <ChevronChip from={payload.fromName} to={payload.toName} />,
      };

    case "TICKET_BRANCH_CHANGED":
      return {
        icon: <FaTag size={10} />,
        dotColor: "neutral",
        title: "Филиал изменён",
        body: <ChevronChip from={payload.fromName} to={payload.toName} />,
      };

    case "TICKET_SUBJECT_CHANGED":
      return {
        icon: <FaTag size={10} />,
        dotColor: "neutral",
        title: "Тема изменена",
        body: <ChevronChip from={payload.from} to={payload.to} />,
      };

    case "TICKET_ASSIGNEE_CHANGED":
      return {
        icon: <FaUserPlus size={10} />,
        dotColor: "accent",
        title: "Назначен ответственный",
        body: <ChevronChip from={payload.fromName} to={payload.toName} accentTo />,
      };

    case "TICKET_CUSTOMER_CHANGED":
      return {
        icon: <FaUserEdit size={10} />,
        dotColor: "neutral",
        title: "Клиент изменён",
        body: <ChevronChip from={payload.fromName} to={payload.toName} />,
      };

    case "TICKET_REPLY_SENT":
      return {
        icon: <FaEnvelope size={10} />,
        dotColor: "accent",
        title: "Ответ клиенту отправлен",
        body: payload.text ? <div className="tv2-tl-text">{payload.text}</div> : null,
      };

    case "TICKET_INTERNAL_NOTE":
      return {
        icon: <FaStickyNote size={10} />,
        dotColor: "warn",
        title: "Внутренняя заметка",
        body: payload.text ? <div className="tv2-tl-text">{payload.text}</div> : null,
      };

    case "TICKET_RESOLVED":
      return {
        icon: <FaCheck size={10} />,
        dotColor: "success",
        title: "Обращение решено",
        body: null,
      };

    case "TICKET_CLOSED":
      return {
        icon: <FaLock size={10} />,
        dotColor: "danger",
        title: payload.auto ? "Обращение закрыто автоматически" : "Обращение закрыто",
        body: payload.reason ? (
          <div className="tv2-tl-text">
            <span style={{ color: "var(--tv2-text-3)" }}>Причина: </span>
            {t(`ticket.closeReason.${payload.reason}`, { defaultValue: payload.reason })}
          </div>
        ) : null,
      };

    case "TICKET_REOPENED":
      return {
        icon: <FaUndoAlt size={10} />,
        dotColor: "info",
        title: "Обращение переоткрыто",
        body: payload.reason ? (
          <div className="tv2-tl-text" style={{ color: "var(--tv2-text-3)" }}>
            Был закрыт с причиной:{" "}
            {t(`ticket.closeReason.${payload.reason}`, {
              defaultValue: payload.reason,
            })}
          </div>
        ) : null,
      };

    case "COMMENT_ADDED":
      return {
        icon: <FaCommentAlt size={9} />,
        dotColor: "neutral",
        title: "Комментарий",
        body: payload.text ? <div className="tv2-tl-text">{payload.text}</div> : null,
      };

    default:
      return {
        icon: <FaCheck size={10} />,
        dotColor: "neutral",
        title: event.eventType,
        body: null,
      };
  }
};

export const ActivityTimeline = ({ ticketID }: ActivityTimelineProps) => {
  const ticketIDStr = String(ticketID);
  const { t } = useTranslation();
  const { data: events, isLoading } = useGetTicketEvents(ticketIDStr);
  const list = (events as Event[] | undefined) || [];

  const { register, handleSubmit, reset, formState } = useForm({
    defaultValues: { text: "" },
    mode: "onChange",
  });
  const [isPosting, setIsPosting] = useState(false);

  const onSubmitComment = async (data: any) => {
    const text = (data.text || "").trim();
    if (!text) {
      toast.error("Комментарий не может быть пустым");
      return;
    }
    if (text.length < 3) {
      toast.error("Комментарий должен быть больше 3 символов");
      return;
    }
    try {
      setIsPosting(true);
      await eventsService.addEvent({
        ticketID: ticketIDStr,
        eventType: "COMMENT_ADDED",
        payload: { text },
      });
      queryClient.invalidateQueries({ queryKey: ["events", "ticket", ticketIDStr] });
      reset();
    } catch (err) {
      console.error(err);
      toast.error("Ошибка при добавлении комментария");
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="tv2-timeline-wrap">
      {/* <div className="tv2-timeline-composer">
                <TextField
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Добавить комментарий…"
                    {...register("text")}
                    fullWidth
                    disabled={isPosting}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <Button
                        type="button"
                        size="small"
                        variant="contained"
                        onClick={handleSubmit(onSubmitComment)}
                        disabled={isPosting || !formState.isValid}
                        startIcon={
                            isPosting ? (
                                <CircularProgress size={11} color="inherit" />
                            ) : (
                                <FaCommentAlt size={10} />
                            )
                        }
                    >
                        Отправить
                    </Button>
                </div>
            </div> */}

      {isLoading ? (
        <div style={{ display: "grid", placeItems: "center", padding: 24 }}>
          <CircularProgress size={20} />
        </div>
      ) : list.length === 0 ? (
        <div className="tv2-empty-hint" style={{ padding: "16px 0" }}>
          Действий пока нет.
        </div>
      ) : (
        <ol className="tv2-timeline">
          {list.map((event) => {
            const r = buildRendered(event, t);
            const actorName = event.actorName || (event.payload || {}).actorName;
            return (
              <li key={event.id} className="tv2-timeline-item">
                <span className={`tv2-timeline-dot dot-${r.dotColor}`}>{r.icon}</span>
                <div className="tv2-timeline-content">
                  <div className="tv2-timeline-head">
                    <span className="tv2-timeline-title">{r.title}</span>
                    {actorName && <span className="tv2-timeline-actor">· {actorName}</span>}
                    <span className="tv2-timeline-time">{toDate(event.createdAt)}</span>
                  </div>
                  {r.body && <div className="tv2-timeline-body">{r.body}</div>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
